import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const tese: ContentBlock<"procedure"> = {
  id: makeId("proc-tese"),
  kind: "procedure",
  tags: ["his", "semen-analysis", "ivf"],
  title: "TESE / Micro-TESE",
  subtitle: "Surgical sperm extraction directly from testicular tissue",
  body: {
    procedureName: "Testicular Sperm Extraction (TESE / Micro-TESE)",
    category: "retrieval",
    description:
      "A surgical procedure to extract sperm directly from testicular tissue when sperm cannot be found in the ejaculate (azoospermia). Micro-TESE uses an operating microscope for more precise extraction and higher success rates, particularly for non-obstructive azoospermia.",
    howItWorks:
      "Under sedation or general anesthesia, a small incision is made in the scrotum. For conventional TESE, small pieces of testicular tissue are biopsied and examined for sperm. For micro-TESE, an operating microscope (15-25x magnification) allows the surgeon to identify seminiferous tubules most likely to contain sperm, reducing tissue damage. Retrieved sperm are used immediately for ICSI or cryopreserved for future use.",
    duration: "30-60 minutes (TESE); 2-4 hours (micro-TESE)",
    costRange: { low: 3000, high: 10000 },
    currency: "USD",
    anesthesia: "sedation",
    recoveryTime: "3-7 days of rest; avoid heavy activity for 2 weeks",
    whatToExpect: [
      "Performed by a reproductive urologist in a surgical center",
      "Coordinated with partner's egg retrieval or for sperm freezing",
      "Scrotal swelling and bruising for several days",
      "Pain managed with ice packs and oral pain medication",
      "Supportive underwear recommended for 1-2 weeks",
      "Sperm retrieval rate: 50-60% for micro-TESE in non-obstructive azoospermia",
    ],
    prepInstructions: [
      "Hormonal evaluation and genetic testing before procedure",
      "No blood thinners or aspirin for 7-10 days before",
      "Arrange for a ride home",
      "Prepare ice packs and supportive underwear at home",
    ],
    risks: [
      "Scrotal swelling and bruising (expected)",
      "Pain (usually well-managed with oral medications)",
      "Infection (uncommon)",
      "Hematoma (uncommon)",
      "Temporary testosterone decrease (rare with micro-TESE)",
      "No sperm found (40-50% of non-obstructive cases)",
    ],
    forWhom: "him",
    frBody: {
      procedureName: "TESE / Micro-TESE",
      description:
        "Geste chirurgical d'extraction de spermatozoïdes directement à partir du tissu testiculaire lorsqu'aucun spermatozoïde n'est retrouvé dans l'éjaculat (azoospermie). La micro-TESE utilise un microscope opératoire pour une extraction plus précise et un meilleur taux de recueil, notamment en cas d'azoospermie non obstructive.",
      howItWorks:
        "Sous sédation ou anesthésie générale, une petite incision scrotale est réalisée. En TESE conventionnelle, de petits fragments de tissu testiculaire sont prélevés et examinés à la recherche de spermatozoïdes. En micro-TESE, un microscope opératoire (grossissement 15-25x) permet d'identifier les tubules séminifères les plus susceptibles de contenir des spermatozoïdes, ce qui limite les lésions tissulaires. Les spermatozoïdes recueillis sont utilisés immédiatement pour une ICSI (injection intracytoplasmique de spermatozoïde) ou cryopréservés.",
      duration: "30 à 60 minutes (TESE) ; 2 à 4 heures (micro-TESE)",
      recoveryTime:
        "3 à 7 jours de repos ; éviter les efforts intenses pendant 2 semaines",
      whatToExpect: [
        "Geste réalisé par un urologue de la reproduction en milieu chirurgical",
        "À coordonner avec la ponction de la partenaire ou en vue d'une congélation des spermatozoïdes",
        "Gonflement et ecchymoses scrotaux pendant plusieurs jours",
        "Douleur gérée par la glace et des antalgiques par voie orale",
        "Port de sous-vêtements soutenants recommandé pendant 1 à 2 semaines",
        "Taux de recueil : 50 à 60 % en micro-TESE dans les azoospermies non obstructives",
      ],
      prepInstructions: [
        "Bilan hormonal et tests génétiques avant le geste",
        "Pas d'anticoagulant ni d'aspirine 7 à 10 jours avant",
        "Prévoyez quelqu'un pour vous raccompagner",
        "Préparez des poches de glace et des sous-vêtements soutenants à la maison",
      ],
      risks: [
        "Gonflement et ecchymoses scrotaux (attendus)",
        "Douleur (généralement bien gérée par les antalgiques oraux)",
        "Infection (peu fréquente)",
        "Hématome (peu fréquent)",
        "Baisse transitoire de testostérone (rare avec la micro-TESE)",
        "Absence de spermatozoïdes (40 à 50 % des azoospermies non obstructives)",
      ],
    },
  },
  sources: [
    {
      title:
        "Diagnosis and Treatment of Infertility in Men: AUA/ASRM Guideline",
      authors: "Schlegel PN, Sigman M, Collura B, et al.",
      journal: "Journal of Urology",
      year: 2020,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/diagnosis-and-treatment-of-infertility-in-men-auaasrm-guideline-part-i-2020/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(tese);
